import { useState, useCallback, useRef, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { useAppSelector } from "../store";

const MAX_RECORDING_DURATION_MS = 5 * 60 * 1000; // 5 minutes

interface STTState {
  isRecording: boolean;
  isTranscribing: boolean;
  error: string | null;
}

export function useAIChatSTT() {
  const [state, setState] = useState<STTState>({
    isRecording: false,
    isTranscribing: false,
    error: null,
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const autoStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idToken = useAppSelector((s) => s.session.idToken);
  const idTokenRef = useRef(idToken);
  useEffect(() => {
    idTokenRef.current = idToken;
  }, [idToken]);

  const { isImpersonating, targetUid } = useAppSelector((s) => s.impersonation);
  const impersonationRef = useRef({ isImpersonating, targetUid });
  useEffect(() => {
    impersonationRef.current = { isImpersonating, targetUid };
  }, [isImpersonating, targetUid]);

  const startRecording = useCallback(async () => {
    try {
      setState({ isRecording: true, isTranscribing: false, error: null });
      chunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4",
      });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.start();

      // Auto-stop after 5 minutes
      autoStopTimerRef.current = setTimeout(() => {
        if (mediaRecorderRef.current?.state === "recording") {
          mediaRecorderRef.current.stop();
        }
      }, MAX_RECORDING_DURATION_MS);
    } catch (err: unknown) {
      setState({
        isRecording: false,
        isTranscribing: false,
        error: err instanceof Error ? err.message : "Microphone access denied",
      });
    }
  }, []);

  const stopRecording = useCallback(
    async (): Promise<string | null> => {
      return new Promise((resolve) => {
        // Clear auto-stop timer
        if (autoStopTimerRef.current) {
          clearTimeout(autoStopTimerRef.current);
          autoStopTimerRef.current = null;
        }

        const mediaRecorder = mediaRecorderRef.current;
        if (!mediaRecorder || mediaRecorder.state === "inactive") {
          setState((prev) => ({ ...prev, isRecording: false }));
          resolve(null);
          return;
        }

        mediaRecorder.onstop = async () => {
          // Stop all tracks
          mediaRecorder.stream.getTracks().forEach((t) => t.stop());
          setState((prev) => ({
            ...prev,
            isRecording: false,
            isTranscribing: true,
          }));

          const blob = new Blob(chunksRef.current, {
            type: mediaRecorder.mimeType,
          });
          chunksRef.current = [];

          try {
            const formData = new FormData();
            formData.append("audio", blob, "recording.webm");

            const apiUrl = import.meta.env.VITE_API_URL;
            const res = await fetch(`${apiUrl}/api/v1/ai/chat/stt`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${idTokenRef.current}`,
                ...(impersonationRef.current.isImpersonating && impersonationRef.current.targetUid
                  ? { "X-Impersonate-Uid": impersonationRef.current.targetUid }
                  : {}),
              },
              body: formData,
            });

            if (!res.ok) {
              throw new Error(`Transcription failed: ${res.status}`);
            }

            const data = await res.json();
            setState({
              isRecording: false,
              isTranscribing: false,
              error: null,
            });
            resolve(data.text || null);
          } catch (err: unknown) {
            Sentry.captureException(err);
            setState({
              isRecording: false,
              isTranscribing: false,
              error: err instanceof Error ? err.message : "Transcription failed",
            });
            resolve(null);
          }
        };

        mediaRecorder.stop();
      });
    },
    []
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoStopTimerRef.current) {
        clearTimeout(autoStopTimerRef.current);
      }
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop());
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  return {
    ...state,
    startRecording,
    stopRecording,
  };
}
